import { z } from "zod";
import type { CompletionGroup } from "./prescription-transfer-review";
import type { MedicalEvidenceTaskKey } from "./medical-evidence";

// TES-909 — what the doctor is asking the patient to complete, in HIS words.
//
// Emelie 2026-09-24, and the whole reason this file exists:
//
//   "Kirill ska aldrig välja mellan 'F-07-sektioner', 'CP-06-uppgifter' eller 'kompletteringsgrupper'."
//   "Systemet sköter den tekniska mappningen bakom kulisserna."
//   "F-07 och CP-06 får förekomma som interna källidentifierare, men inte som handlingsspråk för Kirill eller patienten."
//
// Until now the admin asked him to pick from BOTH internal vocabularies side
// by side — `completionGroups` (F-07 section ids) and `evidenceTasks` (CP-06
// task keys). That is the system's filing system offered as a clinical
// choice. This table is the translation layer, so the two vocabularies stay
// where they belong: in the data.
//
// The seven choices are hers, verbatim and in her order.
export const COMPLETION_NEEDS = [
  "current_medication_and_dose",
  "how_treatment_has_gone",
  "prescription_evidence",
  "blood_pressure_and_pulse",
  "previous_lab_results",
  "new_blood_test",
  "other_medical_information",
] as const;
export const completionNeedSchema = z.enum(COMPLETION_NEEDS);
export type CompletionNeed = z.infer<typeof completionNeedSchema>;

// The doctor-facing label, which is the only string any surface may show him.
export const COMPLETION_NEED_LABEL: Record<CompletionNeed, string> = {
  current_medication_and_dose: "Aktuellt läkemedel och dos",
  how_treatment_has_gone:
    "Hur behandlingen har genomförts, inklusive eventuella uppehåll",
  prescription_evidence: "Recept- eller ordinationsunderlag",
  blood_pressure_and_pulse: "Nytt blodtryck och ny vilopuls",
  previous_lab_results: "Tidigare provsvar",
  new_blood_test: "Nytt blodprov",
  other_medical_information: "Annan medicinsk information",
};

// The mapping. Each need resolves to F-07 sections to reopen, CP-06 tasks to
// raise, or both.
//
// WHICH SIDE A NEED LANDS ON IS HER RULE, NOT OUR TASTE:
//
//   "Ur patientens perspektiv är detta fortfarande ett enda samlat kompletteringsflöde. Frågorna från F-07 utgör själva uppgiften i flödet. Separata rader skapas endast för fristående aktiviteter, exempelvis filuppladdning eller nytt blodprov."
//
// So anything F-07 already asks reopens the section — no duplicate checklist
// row for the same information need — and only what F-07 cannot ask becomes
// its own row. The split matches the history exactly: TES-761 removed blood
// pressure, pulse and earlier labs from F-07 in v4 and made them CP-06 tasks
// (Emelie 2026-09-17), and those are precisely the three needs below that
// carry tasks instead of groups.
//
// `prescription_evidence` is the one that looks like it should be a task and
// is not. She names "filuppladdning" as an example of a standalone row, but
// F-07's own `prescription_evidence` section IS the upload
// (`pt_prescription_upload`, kind `file_upload`), so reopening the section
// keeps it inside the single collected flow rather than splitting it out of
// one. A standalone row is for an upload F-07 has no question for.
//
// NOTHING HERE ADDS `lab_payment`. A new blood test can cost money, but
// "Blodprov är separat och ingår bara när läkaren uttryckligen begär det",
// and TES-763 already settled that a targeted komplettering "skapar ingen
// implicit provbetalning". A payment the patient was never asked to make must
// not appear because a checkbox was ticked.
export const COMPLETION_NEED_MAPPING: Record<
  CompletionNeed,
  {
    groups: readonly CompletionGroup[];
    tasks: readonly MedicalEvidenceTaskKey[];
  }
> = {
  // Both of the first two reopen the SAME F-07 section, and that is a real
  // limitation worth naming rather than hiding: F-07's granularity is
  // section-level, so asking only about interruptions also re-asks drug and
  // dose. Splitting them would need a new F-07 version with its own
  // sections; until then the patient is asked slightly more than the doctor
  // ticked, which is the safe direction of the two.
  current_medication_and_dose: { groups: ["current_treatment"], tasks: [] },
  how_treatment_has_gone: { groups: ["current_treatment"], tasks: [] },
  prescription_evidence: { groups: ["prescription_evidence"], tasks: [] },
  blood_pressure_and_pulse: { groups: [], tasks: ["blood_pressure_pulse"] },
  previous_lab_results: { groups: [], tasks: ["previous_lab_results"] },
  new_blood_test: { groups: [], tasks: ["new_blood_test"] },
  other_medical_information: { groups: ["other_medical"], tasks: [] },
};

// Resolve the doctor's clinical choices into the two stored vocabularies.
//
// Deduplicated, because two needs can share a section: ticking both of the
// first two must reopen `current_treatment` once, not name it twice. Order
// follows the canonical enum so the stored array is stable regardless of the
// order he ticked them in — a decision row that differs only by click order
// would make two identical kompletteringar look different in the audit.
export function completionNeedsToDecision(needs: readonly CompletionNeed[]): {
  completionGroups: CompletionGroup[];
  evidenceTasks: MedicalEvidenceTaskKey[];
} {
  const chosen = new Set(needs);
  const groups = new Set<CompletionGroup>();
  const tasks = new Set<MedicalEvidenceTaskKey>();
  for (const need of COMPLETION_NEEDS) {
    if (!chosen.has(need)) continue;
    for (const g of COMPLETION_NEED_MAPPING[need].groups) groups.add(g);
    for (const t of COMPLETION_NEED_MAPPING[need].tasks) tasks.add(t);
  }
  return {
    completionGroups: [...groups],
    evidenceTasks: [...tasks],
  };
}

// The request as the admin sends it. At least one need — which is the same
// rule the decision schema already enforces one level down, stated here in
// the doctor's vocabulary so the refusal can be phrased in it too:
//
//   "Publicering ska stoppas endast om läkaren inte har valt något kompletteringsbehov alls."
export const completionRequestSchema = z.object({
  needs: z.array(completionNeedSchema).min(1),
  // Patient-visible, as it already is on the decision.
  reason: z.string().trim().min(1).max(2000),
});
export type CompletionRequest = z.infer<typeof completionRequestSchema>;
